import type { FC, PropsWithChildren, ReactNode } from "react";
import { useMemo } from "react";

import { Blocks, isEditing } from "@builder.io/sdk-react";

import { QueryClient, QueryClientProvider } from "@jti/rest-api";
import { ProductCardContainer as UIProductCardContainer } from "@jti/ui/components/ProductCardContainer/ProductCardContainer";

import { getBuilderChildNodes, getBuilderChildrenCount } from "@/libs/builder/utils/getBuilderChildrenCount";

import { Placeholder } from "../Placeholder/Placeholder";

import type { ProductCardContainerBuilderProps } from "./ProductCardContainer.builder";

type ProductCardContainerProps = ProductCardContainerBuilderProps;

const client = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export const ProductCardContainer: FC<PropsWithChildren<ProductCardContainerProps>> = ({
  children,
  builderBlock,
  builderContext,
  builderComponents,
  isCarousel,
  isControlHidden,
  isOverflowEnabled,
  desktopItemCount,
  tabletItemCount,
  title,
  ctaLabel,
  ctaHref,
  attributes,
  isTrial = false,
  isViewItemListEventEnabled = false,
}) => {
  const childrenForUi: ReactNode = useMemo(() => {
    if (builderBlock) {
      return (builderBlock.children ?? [])
        .filter(Boolean)
        .map((childBlock, index) => (
          <Blocks
            key={childBlock.id ?? index}
            blocks={[childBlock]}
            parent={builderBlock.id}
            path={`children.${index}`}
            context={builderContext}
            registeredComponents={builderComponents}
            styleProp={{ height: "100%" }}
          />
        ));
    }
    return getBuilderChildNodes(children);
  }, [builderBlock, builderContext, builderComponents, children]);

  const hasChildren =
    builderBlock != null ? (builderBlock.children?.length ?? 0) > 0 : getBuilderChildrenCount(children) > 0;

  if (!hasChildren && isEditing()) {
    return <Placeholder>Specify Product Card SKU in the Builder.io editor</Placeholder>;
  }
  return (
    <QueryClientProvider client={client}>
      <div {...attributes}>
        <UIProductCardContainer
          isCarousel={isCarousel}
          isControlHidden={isControlHidden}
          isOverflowEnabled={isOverflowEnabled}
          desktopItemCount={desktopItemCount}
          tabletItemCount={tabletItemCount}
          title={title}
          ctaLabel={ctaLabel}
          ctaHref={ctaHref}
          isTrial={isTrial}
          isViewItemListEventEnabled={isViewItemListEventEnabled}
        >
          {childrenForUi}
        </UIProductCardContainer>
      </div>
    </QueryClientProvider>
  );
};
